import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { anchor, parseBlocks } from '../lib/markdown'

const SAFE_LINK = /^(https?:\/\/|\/|#)/
// Images only from the platform's own guideline images (PRJ-17); the page allows no other.
const SAFE_IMAGE = /^\/api\/guidelines\/images\/[0-9a-f-]+$/

/** A link: in-app paths through the router; web addresses in a new tab, without the page's address. */
function link(href: string, label: string, key: number, user: boolean): ReactNode {
  if (href.startsWith('/') && !href.startsWith('/api/')) {
    return (
      <Link key={key} to={href}>
        {label}
      </Link>
    )
  }
  if (!/^https?:/.test(href)) {
    return (
      <a key={key} href={href}>
        {label}
      </a>
    )
  }
  if (!URL.canParse(href)) return label
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow">
      {label}
      {/* What users write shows where it leads (FOR-03). */}
      {user && <small className="link-host"> ({new URL(href).host})</small>}
    </a>
  )
}

/**
 * **bold**, `code`, [links](/path) and ![images](/api/…); links go only to web or in-app addresses.
 * In what users write (`user`), images stay their text.
 */
function inline(text: string, user: boolean): ReactNode[] {
  const parts: ReactNode[] = []
  let last = 0
  for (const match of text.matchAll(/!\[([^\]]*)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g)) {
    parts.push(text.slice(last, match.index))
    const [, alt, src, bold, code, label, href] = match
    if (src !== undefined) parts.push(!user && SAFE_IMAGE.test(src) ? <img key={match.index} src={src} alt={alt} loading="lazy" /> : alt)
    else if (bold !== undefined) parts.push(<strong key={match.index}>{bold}</strong>)
    else if (code !== undefined) parts.push(<code key={match.index}>{code}</code>)
    else if (href && SAFE_LINK.test(href)) parts.push(link(href, label!, match.index, user))
    else parts.push(label)
    last = match.index + match[0].length
  }
  parts.push(text.slice(last))
  return parts
}

/**
 * Renders the Markdown subset as elements, never as HTML, so a text cannot inject markup. Text users
 * write (`user`, such as forum posts) keeps its line breaks and shows no images (FOR-03). With
 * `anchored`, sections (##) have addresses a list of contents links to.
 */
export function Markdown({ text, user = false, anchored = false }: { text: string; user?: boolean; anchored?: boolean }) {
  return (
    <div className={user ? 'markdown user-text' : 'markdown'}>
      {parseBlocks(text, { breaks: user }).map((block, index) => {
        if (block.kind === 'heading') {
          const Heading = `h${block.level}` as const
          const id = anchored && block.level === 2 ? anchor(block.text) : undefined
          return (
            <Heading key={index} id={id}>
              {inline(block.text, user)}
            </Heading>
          )
        }
        if (block.kind === 'paragraph') return <p key={index}>{inline(block.text, user)}</p>
        const List = block.ordered ? 'ol' : 'ul'
        return (
          <List key={index}>
            {block.items.map((item, position) => (
              <li key={position}>{inline(item, user)}</li>
            ))}
          </List>
        )
      })}
    </div>
  )
}
